import { Directive, ElementRef, HostListener, Input, OnChanges, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CurrencyPipe } from '@angular/common';
import { asNumber, isNull } from '../../shared';

const DEFAULT_MAX = 1000000000;

@Directive({
  selector: '[appIntegerInput]',
  exportAs: 'appIntegerInput',
})
export class IntegerInputDirective implements OnChanges, OnInit {
  @Input()
  public min: number = null;

  @Input()
  public max: number = DEFAULT_MAX;

  @Input()
  public log: boolean = false;

  @Input()
  public allowNegative: boolean = false;

  @Input('appIntegerInput')
  public set value(value: number) {
    this.refreshView(asNumber(value));
  }

  @HostListener('input', ['$event'])
  public onInput = (): void => {
    const { ref, value$, min, max, allowNegative, getFormattedValue } = this;
    const elem = ref.nativeElement;
    const str = elem.value;
    const caretPosition = elem.selectionStart;
    const formatMatch = allowNegative ? /^(-)?[\d,]*$/ : /^[\d,]*$/;

    const isBadFormat = (): boolean => {
      return !formatMatch.test(str) || /,,/.test(str);
    };

    const applyChange = (val: number): void => {
      value$.next(asNumber(val));
    };

    const discardChange = (nextVal: number = null): void => {
      !isNull(nextVal) && applyChange(nextVal);
      elem.value = getFormattedValue();
      elem.setSelectionRange(caretPosition, caretPosition);
    };

    if (isBadFormat()) {
      discardChange();
    } else {
      const value = asNumber(str);

      if (!isNull(min) && value < min) {
        discardChange(min);
      } else if (!isNull(max) && value > max) {
        discardChange(max);
      } else {
        applyChange(value);
      }
    }
  };

  @HostListener('change', ['$event'])
  public onChange = () => {
    const { ref, getFormattedValue } = this;
    const elem = ref.nativeElement;

    elem.value = getFormattedValue();
  };

  @HostListener('focus', ['$event'])
  public onFocus = (): void => {
    const { ref } = this;
    const elem: HTMLInputElement = ref.nativeElement;

    elem.selectionStart = 0;
    elem.selectionEnd = elem.value.length;
  };

  private readonly value$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  public getFormattedValue = (): string => {
    const { currencyPipe, value$ } = this;
    const { value } = value$;

    return currencyPipe.transform(value, '', '', '1.0');
  };

  public getValue = () => {
    return this.value$.value;
  };

  public refreshView = (value: number, forceUpdate: boolean = false): void => {
    const { ref, value$, getFormattedValue } = this;
    const nextValue = asNumber(value || 0);

    if (forceUpdate || `${nextValue}` !== `${value$.value}`) {
      value$.next(nextValue);
      ref.nativeElement.value = getFormattedValue();
    }
  };

  constructor(private currencyPipe: CurrencyPipe, private ref: ElementRef) {}

  ngOnInit(): void {
    this.refreshView(this.value$.value, true);
  }

  ngOnChanges(): void {
    this.refreshView(this.value$.value);
  }
}
